import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { DashboardSettingsResponseDto } from '@api'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { Toggle } from '@/components/ui/Toggle.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { debounce } from '@/utils/helpers'

import { useAutomationsMutations } from '../../hooks/use-automations-mutations'
import { AutoFollowTable } from '../auto-follow-table'

interface AutoFollowFormProps {
  data: Pick<
    DashboardSettingsResponseDto,
    'autoFollowExpiredFans' | 'autoFollowMinimumSpend'
  >
}

type AutoFollowFormValues = z.infer<typeof AutoFollowFormSchema>

const AutoFollowFormSchema = z.object({
  enabled: z.boolean(),
  minimumSpend: z.number(),
})

export const AutoFollowForm = ({ data }: AutoFollowFormProps) => {
  const { setAutoFollowMinimumSpending, enableAutoFollow } =
    useAutomationsMutations()

  const { setSettingsData } = useSettingsContext()

  const toast = useToast()

  const [minimumSpendEnabled, setMinimumSpendEnabled] = useState(
    data.autoFollowMinimumSpend! > 0,
  )

  const defaultFormValues = useMemo(
    () => ({
      enabled: data.autoFollowExpiredFans,
      minimumSpend: data.autoFollowMinimumSpend,
    }),
    [data],
  )

  const form = useForm<AutoFollowFormValues>({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  })

  const { control, reset } = form

  useEffect(
    () => {
      reset(defaultFormValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultFormValues],
  )

  const handleEnabledChange = useCallback(
    (value: ChangeEvent<HTMLInputElement>) => {
      const checkedState = value.target.checked

      enableAutoFollow
        .mutateAsync({
          state: checkedState,
        })
        .then(() => {
          let state = 'enabled'

          if (checkedState === false) {
            state = 'disabled'
          }

          setSettingsData((prevState) => ({
            ...prevState,
            autoFollowExpiredFans: checkedState,
          }))

          toast.success(`Successfully ${state} Auto-Follow`)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSpendingChange = useCallback(
    debounce((targetValue: string | number) => {
      const value = parseInt(targetValue.toString())

      if (isNaN(value) || value < 0) {
        return
      }

      setAutoFollowMinimumSpending.mutateAsync(value).then(() => {
        form.setValue('minimumSpend', value)
        setSettingsData((prevState) => ({
          ...prevState,
          autoFollowMinimumSpend: value,
        }))

        if (value === 0) {
          toast.success('Successfully disabled minimum spend')

          return
        }

        toast.success('Successfully set minimum spend')
      })
    }, 800),
    [],
  )

  return (
    <FormProvider {...form}>
      <Page
        title="Auto-Follow"
        pageClassName="pb-0"
        description={
          <div className="flex items-center gap-1">
            <span>Automatically follow expired fans to win them back</span>
          </div>
        }
        headerOptions={
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <Toggle
                checked={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  handleEnabledChange(value)
                }}
              />
            )}
          />
        }
      >
        <hr />

        {form.getValues('enabled') && (
          <>
            <SettingsOption
              title="Minimum Spend"
              description="Only follow expired fans with a certain amount of spend"
              biggerTitle
              mobileVertical
            >
              <div className="flex items-center gap-4">
                {minimumSpendEnabled && (
                  <Controller
                    name="minimumSpend"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        className="w-32"
                        type="number"
                        value={field.value}
                        prefix={
                          <span className="text-text-sub flex items-center">
                            $
                          </span>
                        }
                        onChange={(value) => {
                          field.onChange(value)
                          handleDebouncedSpendingChange(value.target.value)
                        }}
                        onBlur={(event) => {
                          if (event.target.value === '') {
                            form.setValue(
                              'minimumSpend',
                              data.autoFollowMinimumSpend!,
                            )
                          }
                        }}
                      />
                    )}
                  />
                )}
                <Toggle
                  name="minimumSpendEnabled"
                  checked={minimumSpendEnabled}
                  onChange={(value) => {
                    const checked = value.target.checked

                    setMinimumSpendEnabled(checked)
                    if (checked) {
                      form.setValue('minimumSpend', 200)
                      handleDebouncedSpendingChange(200)
                      // handleEnabledChange(value);
                    } else {
                      form.setValue('minimumSpend', 0)
                      handleDebouncedSpendingChange(0)
                    }
                  }}
                />
              </div>
            </SettingsOption>
            <hr />
          </>
        )}

        <AutoFollowTable />
      </Page>
    </FormProvider>
  )
}
